import * as Sentry from '@sentry/browser';
import * as _ from 'lodash';
import * as React from 'react';
import {Redirect, useLocation} from 'react-router-dom';
import {Modal} from 'semantic-ui-react';
import 'whatwg-fetch';
import ErrorPage from './components/ErrorPage';
import './components/vis/ReactVis.css';
import Loader from './components/WandbLoader';
import * as CGReact from './components/Panel2/cg/cgreact';
import './css/App.less';
import './css/Components.less';
import './react-autosuggest.css';
import {auth, history, isInJupyterNotebook} from './setup';
import {displayError} from './state/global/actions';
import {InteractStateProvider} from './state/views/interactState/context';
import {useDispatch, useSelector} from './state/hooks';
import * as ViewerHooks from './state/viewer/hooks';
import {hideIntercom, showIntercom} from './util/analytics';
import makeComp from './util/profiler';
import {captureError, shouldReloadOnError} from './util/integrations';
import {mouseListenersStart} from './util/mouse';
import ErrorPortal from './components/ErrorPortal';
import {useActivityDetectionLoop} from './state/polling/hooks';
import {clearAuthRedirect, setAuthRedirect} from './util/auth';
import NoMatch from './components/NoMatch';
import {viewerUsingAdminPrivileges} from './util/admin';
import {PUBLISHED_PROJECT_NAME} from './util/constants';
import {disableBeamer, openBeamerOnLoad} from './util/beamer';
import {RouteWithLayout} from './routes';
import {
  galleryTagFromQS,
  getReportGalleryPathParams,
  isOnReportGallery,
  useInitGalleryTagSections,
  sectionFromFormattedTag,
  GalleryTagSection,
  formatGalleryTag,
  useGalleryTagSections,
} from './util/gallery';
import {isOnReportView} from './util/url';
import startTrace from './util/trace';
import {useEffect} from 'react';

interface AppProps {
  children: React.ReactNode;
}

type AllAppProps = AppProps & ReturnType<typeof useAppProps>;

class App extends React.Component<AllAppProps> {
  componentDidCatch(error: any, info: any) {
    captureError(error, 'app_componentdidcatch');
    this.props.dispatch(displayError(error));
    if (!shouldReloadOnError()) {
      Sentry.showReportDialog();
    }
  }

  componentDidMount() {
    mouseListenersStart();

    if (isInJupyterNotebook()) {
      hideIntercom();
    }
  }

  componentWillUnmount() {
    if (isInJupyterNotebook()) {
      showIntercom();
    }
  }

  render() {
    const {
      viewerQuery,
      galleryTagSectionsQuery,
      error,
      showErrorPortal,
      errorPortalContent,
      children,
    } = this.props;
    // This is required, downstream components assume viewer is loaded.
    if (viewerQuery.loading || galleryTagSectionsQuery.loading) {
      return <Loader />;
    }
    const {viewer} = viewerQuery;
    const trialExpiredModal = undefined;
    // TODO(aswanberg): Disable teams in the backend.
    const blockList = ['luminar'];

    if (viewer && viewer.teams) {
      const teams = viewer.teams.edges;
      if (_.find(teams, t => _.includes(blockList, t.node.name)) != null) {
        return (
          <Modal dimmer="blurring" open={true}>
            <Modal.Header>Trial expired</Modal.Header>
            <Modal.Content>
              The trial period for this account has expired. Please work with
              Weights & Biases to setup payment for continued usage.
            </Modal.Content>
          </Modal>
        );
      }
    }
    const classes = [];
    if (isInJupyterNotebook()) {
      classes.push('jupyter');
    }
    if (
      viewer &&
      viewer.userInfo &&
      viewer.userInfo.bio &&
      viewer.userInfo.bio.toLowerCase().includes('night')
    ) {
      classes.push('night-mode');
    }
    // Enforce user to choose a username
    const isAtSignup = window.location.pathname === '/signup';
    const isAtLogout = window.location.pathname === '/logout';
    const isUsernameRequired = !(isAtLogout || isAtSignup);
    const hasNoUsername =
      viewer &&
      (!viewer.entity || viewer.entity === 'models' || !viewer.username);
    const redirectToOnboarding = isUsernameRequired && hasNoUsername;
    if (redirectToOnboarding) {
      // store what they were trying to access so we can redirect on signup
      const redirect = window.location.href.replace(window.location.origin, '');
      setAuthRedirect(redirect);
      // We disable Beamer auto-open for first time signups for a day
      disableBeamer();
      return <Redirect to="/signup" />;
    }
    // disable beamer on the email verification page
    if (
      error != null &&
      error.code === 403 &&
      error.message &&
      typeof error.message === 'string' &&
      error.message.match('Email must be verified')
    ) {
      disableBeamer();
    }
    openBeamerOnLoad();

    const usingAdminPowers = viewer?.admin && viewerUsingAdminPrivileges();
    const onPublishedEditPage = history.location.pathname.includes(
      PUBLISHED_PROJECT_NAME
    );
    const onHiddenPage = !usingAdminPowers && onPublishedEditPage;

    return (
      <div className={'app-root ' + classes.join(' ')}>
        {trialExpiredModal}
        {error ? (
          <RouteWithLayout
            error={error}
            component={ErrorPage}
            auth={undefined}
          />
        ) : onHiddenPage ? (
          <RouteWithLayout component={NoMatch} />
        ) : (
          children
        )}
        <ErrorPortal open={showErrorPortal}>{errorPortalContent}</ErrorPortal>
      </div>
    );
  }
}

function useAppProps() {
  // DO NOT REMOVE THIS useOnLocationChange()
  // We need it to re-render App on every location change,
  // which is necessary due to how routes are being rendered.
  useOnLocationChange();

  const viewerQuery = ViewerHooks.useInitViewer();
  const galleryTagSectionsQuery = useInitGalleryTagSections();
  const dispatch = useDispatch();
  // Note: Very strange... if the selector passed in to the next line
  // (to grab state.global.error) is a globally defined function instead
  // of an inline function, we always get undefined back, at least after
  // faking a login error. This could be a serious issue but I haven't
  // been able to figure out the cause. Defining it inline at least
  // gives the correct result.
  const error = useSelector(state => state.global.error);
  const showErrorPortal = useSelector(state => state.global.showErrorPortal);
  const errorPortalContent = useSelector(
    state => state.global.errorPortalContent
  );

  return {
    error,
    showErrorPortal,
    errorPortalContent,
    dispatch,
    viewerQuery,
    galleryTagSectionsQuery,
  };
}

export default makeComp(
  (props: AppProps) => {
    const selectedProps = useAppProps();

    // This starts the loop that detects when the tab is backgrounded or
    // the user is inactive, which is used to control polling.
    useActivityDetectionLoop();

    return (
      <CGReact.ComputeGraphContextProvider>
        <InteractStateProvider>
          <App {...props} {...selectedProps} />
        </InteractStateProvider>
      </CGReact.ComputeGraphContextProvider>
    );
  },
  {id: 'App'}
);

function addGalleryTagData(
  tagSections: readonly GalleryTagSection[],
  pageViewProps: {[key: string]: string}
): void {
  const galleryPathParams = getReportGalleryPathParams(tagSections);

  let tag: string | null = null;
  if (isOnReportGallery() && galleryPathParams != null) {
    tag = galleryPathParams.tag;
    if (tag) {
      pageViewProps.value = 'Tag Home';
    }
  } else if (isOnReportView()) {
    tag = galleryTagFromQS();
  }

  if (tag) {
    const formattedTag = formatGalleryTag(tagSections, tag);
    const section = sectionFromFormattedTag(tagSections, formattedTag);
    pageViewProps.category = 'Tag';
    if (section) {
      pageViewProps.action = section;
    }
    pageViewProps.label = formattedTag;
  }
}

function trackPageView(tagSections: readonly GalleryTagSection[]): void {
  const properties: {[key: string]: string} = {};
  addGalleryTagData(tagSections, properties);
  window.analytics.page(properties);
}

function useOnLocationChange(): void {
  useLocation();

  const tagSections = useGalleryTagSections();
  useEffect(() => {
    if (tagSections.length > 0) {
      trackPageView(tagSections);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href, tagSections]);

  useEffect(() => {
    startTrace();
    setRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href]);
}

export function setRedirect(): void {
  const redirectTo = window.location.href.replace(window.location.origin, '');
  if (!auth.loggedIn()) {
    setAuthRedirect(redirectTo);
  } else if (!redirectTo.startsWith('/signup')) {
    // after onboarding flow, users should be redirected to the page they landed on before signup

    // First time local users enter an account reset flow so we shouldn't clear their redirect
    if (auth.claims == null || auth.claims.reset !== true) {
      clearAuthRedirect();
    }
  }
}
